/**
 * Custom Embroidery Artwork Fee
 * ------------------------------------------------------------
 * Rule:
 *   1–11 tagged custom embroidered hats => add ONE $25 fee line
 *   12+ tagged custom embroidered hats   => remove/waive fee
 *   0 tagged custom embroidered hats     => remove fee
 *
 * Presentation:
 *   - Keep the real fee line in Shopify cart data for subtotal/checkout.
 *   - Hide only the fee product row in storefront cart/cart drawer/mini cart.
 *   - Show a one-time modal when the fee is newly applied.
 *   - Show a subtle cart notice while the fee is active.
 *
 * Fee variant ID: 43867314290778
 * Qualifying product tag: custom-embroidered-hat
 *
 * This file intentionally does not replace product forms or Globo Product
 * Options behavior. It observes Shopify cart mutations and synchronizes the
 * fee afterward.
 */

(function () {
  'use strict';

  if (window.__CUSTOM_ARTWORK_FEE_LOADED__) return;
  window.__CUSTOM_ARTWORK_FEE_LOADED__ = true;

  var CONFIG = Object.freeze({
    feeVariantId: 43867314290778,
    qualifyingTag: 'custom-embroidered-hat',
    minFeeQuantity: 1,
    maxFeeQuantity: 11,
    debounceMs: 260,
    cartSettleMs: 220,
    modalTitle: 'Artwork Fee Applied',
    modalMessage:
      'A one-time $25 artwork fee has been applied to your custom embroidered hat order. This fee is automatically waived when ordering 12 or more custom embroidered hats.',
    modalButton: 'Got it',
    cartNotice: 'Includes $25 Custom Embroidery Artwork Fee',
    debug: false
  });

  var SOURCE = 'custom-artwork-fee';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var nativeFetch = window.fetch.bind(window);
  var tagCache = new Map();
  var syncTimer = null;
  var syncPromise = null;
  var syncQueued = false;
  var lastReason = 'initial';
  var lastPresentationState = null;
  var presentationTimer = null;
  var domObserver = null;
  var modalOpen = false;

  function log() {
    if (!CONFIG.debug || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[Artwork Fee]');
    console.log.apply(console, args);
  }

  function errorLog() {
    if (!window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[Artwork Fee]');
    console.error.apply(console, args);
  }

  function route(path) {
    return ROOT + String(path || '').replace(/^\//, '');
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function variantIdOf(item) {
    return Number(item && (item.variant_id || item.id)) || 0;
  }

  function isFeeItem(item) {
    return variantIdOf(item) === CONFIG.feeVariantId;
  }

  function isCartMutationUrl(input) {
    var value = '';

    if (typeof input === 'string') {
      value = input;
    } else if (input && typeof input.url === 'string') {
      value = input.url;
    }

    if (!value) return false;

    try {
      var url = new URL(value, window.location.origin);
      return /\/cart\/(add|change|update|clear)(?:\.js)?\/?$/i.test(url.pathname);
    } catch (e) {
      return /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/i.test(value);
    }
  }

  async function fetchJson(url, options) {
    var response = await nativeFetch(url, options || {});

    if (!response.ok) {
      var body = '';
      try {
        body = await response.text();
      } catch (e) {
        body = '';
      }
      throw new Error('Request failed (' + response.status + '): ' + body.slice(0, 250));
    }

    return response.json();
  }

  async function getCart() {
    return fetchJson(route('cart.js'), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) {
      return tags.map(function (tag) {
        return String(tag).trim().toLowerCase();
      });
    }

    if (typeof tags === 'string') {
      return tags.split(',').map(function (tag) {
        return tag.trim().toLowerCase();
      });
    }

    return [];
  }

  function getHandle(item) {
    if (item && item.handle) return String(item.handle);

    var itemUrl = item && item.url ? String(item.url) : '';
    var match = itemUrl.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function productHasQualifyingTag(handle) {
    if (!handle) return Promise.resolve(false);

    if (tagCache.has(handle)) {
      return tagCache.get(handle);
    }

    var lookup = fetchJson(route('products/' + encodeURIComponent(handle) + '.js'), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'default',
      headers: {
        Accept: 'application/json'
      }
    })
      .then(function (product) {
        var tags = normalizeTags(product && product.tags);
        return tags.indexOf(CONFIG.qualifyingTag.toLowerCase()) !== -1;
      })
      .catch(function (err) {
        tagCache.delete(handle);
        errorLog('Could not inspect product tags for "' + handle + '".', err);
        return false;
      });

    tagCache.set(handle, lookup);
    return lookup;
  }

  async function analyzeCart(cart) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var feeLines = [];
    var qtyByHandle = new Map();

    items.forEach(function (item) {
      if (isFeeItem(item)) {
        feeLines.push(item);
        return;
      }

      var handle = getHandle(item);
      if (!handle) return;

      var quantity = Math.max(0, Number(item.quantity) || 0);
      qtyByHandle.set(handle, (qtyByHandle.get(handle) || 0) + quantity);
    });

    var handles = Array.from(qtyByHandle.keys());
    var tagResults = await Promise.all(
      handles.map(async function (handle) {
        return {
          handle: handle,
          qualifying: await productHasQualifyingTag(handle)
        };
      })
    );

    var customHatQuantity = tagResults.reduce(function (total, result) {
      if (!result.qualifying) return total;
      return total + (qtyByHandle.get(result.handle) || 0);
    }, 0);

    return {
      customHatQuantity: customHatQuantity,
      feeLines: feeLines
    };
  }

  async function addFee() {
    log('Adding artwork fee.');

    return fetchJson(route('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: CONFIG.feeVariantId,
            quantity: 1
          }
        ]
      })
    });
  }

  async function changeLine(lineKey, quantity) {
    return fetchJson(route('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity
      })
    });
  }

  async function enforceFeeState(shouldHaveFee, feeLines) {
    var changed = false;
    var feeAdded = false;
    var lines = Array.isArray(feeLines) ? feeLines.slice() : [];

    if (!shouldHaveFee) {
      for (var i = 0; i < lines.length; i += 1) {
        if (!lines[i] || !lines[i].key) continue;
        await changeLine(lines[i].key, 0);
        changed = true;
      }
      return { changed: changed, feeAdded: false };
    }

    if (lines.length === 0) {
      await addFee();
      return { changed: true, feeAdded: true };
    }

    var primary = lines[0];

    for (var j = lines.length - 1; j >= 1; j -= 1) {
      if (!lines[j] || !lines[j].key) continue;
      await changeLine(lines[j].key, 0);
      changed = true;
    }

    if (primary && primary.key && Number(primary.quantity) !== 1) {
      await changeLine(primary.key, 1);
      changed = true;
    }

    return { changed: changed, feeAdded: feeAdded };
  }

  function cssEscape(value) {
    var str = String(value == null ? '' : value);
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(str);
    }
    return str.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
  }

  function getFeeIdentity(cart) {
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var feeItems = items.filter(isFeeItem);
    var keys = [];
    var handles = [];

    feeItems.forEach(function (item) {
      if (item && item.key) keys.push(String(item.key));
      var handle = getHandle(item);
      if (handle && handles.indexOf(handle) === -1) handles.push(handle);
    });

    return {
      keys: keys,
      handles: handles,
      variantId: String(CONFIG.feeVariantId)
    };
  }

  function closestCartRow(node) {
    if (!node || !node.closest) return null;

    return node.closest(
      [
        '[data-cart-item]',
        '[data-cart-item-key]',
        '[data-line-item]',
        '[data-line-key]',
        '[data-item-key]',
        'cart-item',
        '.cart-item',
        '.m-cart-item',
        '.cart__item',
        '.cart-drawer__item',
        '.m-cart-drawer__item',
        '.mini-cart__item',
        '.cart-item-row',
        'tr',
        'li'
      ].join(',')
    );
  }

  function markHiddenFeeRow(row) {
    if (!row || row.nodeType !== 1) return;
    row.setAttribute('data-artwork-fee-hidden-row', 'true');
    row.setAttribute('aria-hidden', 'true');
    row.style.setProperty('display', 'none', 'important');
  }

  function hideFeeRows(cart) {
    var identity = getFeeIdentity(cart);
    var candidates = new Set();

    function addMatches(selector) {
      if (!selector) return;
      try {
        document.querySelectorAll(selector).forEach(function (node) {
          candidates.add(node);
        });
      } catch (e) {
        // Invalid selector in an unusual theme should not break the fee logic.
      }
    }

    var variant = cssEscape(identity.variantId);
    [
      '[data-variant-id="' + variant + '"]',
      '[data-variant="' + variant + '"]',
      '[data-product-variant-id="' + variant + '"]',
      '[data-id="' + variant + '"]',
      'input[value="' + variant + '"][name="id"]',
      'input[value="' + variant + '"][name="variant_id"]'
    ].forEach(addMatches);

    identity.keys.forEach(function (key) {
      var escapedKey = cssEscape(key);
      [
        '[data-key="' + escapedKey + '"]',
        '[data-line-key="' + escapedKey + '"]',
        '[data-item-key="' + escapedKey + '"]',
        '[data-cart-item-key="' + escapedKey + '"]'
      ].forEach(addMatches);

      try {
        document.querySelectorAll('a[href*="/cart/change"], a[href*="/cart/remove"]').forEach(function (link) {
          var href = link.getAttribute('href') || '';
          if (href.indexOf(key) !== -1 || href.indexOf(encodeURIComponent(key)) !== -1) {
            candidates.add(link);
          }
        });
      } catch (e) {
        // No-op.
      }
    });

    identity.handles.forEach(function (handle) {
      var encodedHandle = encodeURIComponent(handle);
      try {
        document.querySelectorAll('a[href*="/products/' + encodedHandle + '"]').forEach(function (link) {
          candidates.add(link);
        });
      } catch (e) {
        // No-op.
      }

      // Some themes keep the unencoded handle in href/data attributes.
      try {
        document.querySelectorAll('a[href*="/products/' + cssEscape(handle) + '"]').forEach(function (link) {
          candidates.add(link);
        });
      } catch (e) {
        // No-op.
      }
    });

    candidates.forEach(function (node) {
      var row = closestCartRow(node);
      if (row) markHiddenFeeRow(row);
    });

    // Last-resort fallback only if no stable cart metadata/link is exposed by
    // the theme. This is intentionally not the primary identification method.
    if (identity.handles.length > 0 && document.querySelectorAll('[data-artwork-fee-hidden-row="true"]').length === 0) {
      var possibleRows = document.querySelectorAll(
        '[data-cart-item], [data-line-item], cart-item, .cart-item, .m-cart-item, .cart__item, .cart-drawer__item, .m-cart-drawer__item, .mini-cart__item, tr, li'
      );
      possibleRows.forEach(function (row) {
        var text = (row.textContent || '').replace(/\s+/g, ' ').trim();
        if (text.indexOf('Custom Embroidery Artwork Fee') !== -1) {
          markHiddenFeeRow(row);
        }
      });
    }
  }

  function removeStaleHiddenMarkers() {
    document.querySelectorAll('[data-artwork-fee-hidden-row="true"]').forEach(function (row) {
      if (!document.documentElement.contains(row)) return;
      // Keep the marker; rows are normally replaced by the theme after cart
      // rendering. We never unhide a real fee row while the script is active.
    });
  }

  function ensureModalStyles() {
    if (document.getElementById('artwork-fee-modal-styles')) return;

    var style = document.createElement('style');
    style.id = 'artwork-fee-modal-styles';
    style.textContent =
      '#ArtworkFeeModal{position:fixed;inset:0;z-index:2147483000;display:none;align-items:center;justify-content:center;padding:20px;}' +
      '#ArtworkFeeModal.is-open{display:flex;}' +
      '#ArtworkFeeModal .afm-overlay{position:absolute;inset:0;background:rgba(0,0,0,.48);}' +
      '#ArtworkFeeModal .afm-dialog{position:relative;width:min(92vw,520px);background:#fff;color:#1f2937;border-radius:12px;box-shadow:0 24px 70px rgba(0,0,0,.28);padding:30px 28px 26px;z-index:1;text-align:left;}' +
      '#ArtworkFeeModal .afm-close{position:absolute;top:10px;right:12px;width:38px;height:38px;border:0;background:transparent;color:inherit;font-size:28px;line-height:1;cursor:pointer;border-radius:50%;}' +
      '#ArtworkFeeModal .afm-close:hover{background:rgba(0,0,0,.06);}' +
      '#ArtworkFeeModal .afm-title{margin:0 42px 12px 0;font:inherit;font-size:24px;font-weight:700;line-height:1.25; color:#1f2937;}' +
      '#ArtworkFeeModal .afm-message{margin:0 0 22px;font:inherit;font-size:15px;line-height:1.65;color:inherit;opacity:.88;}' +
      '#ArtworkFeeModal .afm-button{display:inline-flex;align-items:center;justify-content:center;min-width:110px;min-height:44px;padding:10px 22px;border:0;border-radius:6px;background:#1d355d;color:#fff;font:inherit;font-weight:600;cursor:pointer;}' +
      '#ArtworkFeeModal .afm-button:hover{filter:brightness(.95);}' +
      '.artwork-fee-cart-notice{margin:8px 0 10px;font:inherit;font-size:13px;line-height:1.45;opacity:.82;}' +
      '@media(max-width:540px){#ArtworkFeeModal{padding:16px;}#ArtworkFeeModal .afm-dialog{padding:26px 20px 22px;border-radius:10px;}#ArtworkFeeModal .afm-title{font-size:21px;}#ArtworkFeeModal .afm-message{font-size:14px;}}';

    document.head.appendChild(style);
  }

  function ensureModal() {
    ensureModalStyles();

    var existing = document.getElementById('ArtworkFeeModal');
    if (existing) return existing;

    var modal = document.createElement('div');
    modal.id = 'ArtworkFeeModal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'ArtworkFeeModalTitle');
    modal.setAttribute('aria-hidden', 'true');

    modal.innerHTML =
      '<div class="afm-overlay" data-afm-close></div>' +
      '<div class="afm-dialog" role="document">' +
      '<button type="button" class="afm-close" aria-label="Close" data-afm-close>&times;</button>' +
      '<h2 class="afm-title" id="ArtworkFeeModalTitle"></h2>' +
      '<p class="afm-message"></p>' +
      '<button type="button" class="afm-button" data-afm-close></button>' +
      '</div>';

    modal.querySelector('.afm-title').textContent = CONFIG.modalTitle;
    modal.querySelector('.afm-message').textContent = CONFIG.modalMessage;
    modal.querySelector('.afm-button').textContent = CONFIG.modalButton;

    modal.addEventListener('click', function (event) {
      if (event.target && event.target.hasAttribute('data-afm-close')) {
        closeModal();
      }
    });

    document.body.appendChild(modal);
    return modal;
  }

  function openModal() {
    if (modalOpen) return;

    var modal = ensureModal();
    modalOpen = true;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');

    var closeButton = modal.querySelector('.afm-close');
    if (closeButton && closeButton.focus) {
      window.setTimeout(function () {
        try {
          closeButton.focus({ preventScroll: true });
        } catch (e) {
          closeButton.focus();
        }
      }, 30);
    }
  }

  function closeModal() {
    var modal = document.getElementById('ArtworkFeeModal');
    if (!modal) return;

    modalOpen = false;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
  }

  function installModalKeyboardHandler() {
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modalOpen) {
        closeModal();
      }
    });
  }

  function getCartScopeRoots() {
    var selectors = [
      '#MainContent',
      '#CartDrawer',
      'cart-drawer',
      '[id*="CartDrawer"]',
      '[class*="cart-drawer"]',
      '[class*="cart__drawer"]',
      '[data-cart-drawer]',
      '[data-cart-container]',
      '[data-cart-page]',
      '.cart',
      '.m-cart'
    ];

    var roots = [];
    selectors.forEach(function (selector) {
      try {
        document.querySelectorAll(selector).forEach(function (node) {
          if (roots.indexOf(node) === -1) roots.push(node);
        });
      } catch (e) {
        // No-op.
      }
    });

    if (roots.length === 0 && document.body) roots.push(document.body);
    return roots;
  }

  function findSubtotalRows(root) {
    var matches = [];
    var directSelectors = [
      '[data-cart-subtotal]',
      '[data-subtotal]',
      '.cart__subtotal',
      '.cart-subtotal',
      '.m-cart__subtotal',
      '.cart-drawer__subtotal',
      '.m-cart-drawer__subtotal',
      '.cart-summary__subtotal',
      '.cart__summary-total',
      '.m-cart__summary-total'
    ];

    directSelectors.forEach(function (selector) {
      try {
        root.querySelectorAll(selector).forEach(function (node) {
          var row = node.closest(
            '.cart__subtotal, .cart-subtotal, .m-cart__subtotal, .cart-drawer__subtotal, .m-cart-drawer__subtotal, .cart-summary__subtotal, .cart__summary-total, .m-cart__summary-total, .cart__footer, .cart-drawer__footer, .m-cart-drawer__footer, [data-cart-footer], div, p'
          ) || node;
          if (matches.indexOf(row) === -1) matches.push(row);
        });
      } catch (e) {
        // No-op.
      }
    });

    if (matches.length === 0) {
      try {
        root.querySelectorAll('span,div,p,strong,b').forEach(function (node) {
          if (node.children.length > 3) return;
          var text = (node.textContent || '').replace(/\s+/g, ' ').trim();
          if (!/^Subtotal\b/i.test(text)) return;

          var row = node.closest(
            '.cart__subtotal, .cart-subtotal, .m-cart__subtotal, .cart-drawer__subtotal, .m-cart-drawer__subtotal, .cart-summary__subtotal, .cart__summary-total, .m-cart__summary-total, [data-cart-footer], .cart__footer, .cart-drawer__footer, .m-cart-drawer__footer, div, p'
          ) || node;

          if (matches.indexOf(row) === -1) matches.push(row);
        });
      } catch (e) {
        // No-op.
      }
    }

    return matches;
  }

  function updateCartNotice(active) {
    ensureModalStyles();

    if (!active) {
      document.querySelectorAll('[data-artwork-fee-cart-notice]').forEach(function (notice) {
        notice.remove();
      });
      return;
    }

    var roots = getCartScopeRoots();
    roots.forEach(function (root) {
      var subtotalRows = findSubtotalRows(root);
      subtotalRows.forEach(function (subtotalRow) {
        var parent = subtotalRow.parentElement;
        if (!parent) return;

        var existing = parent.querySelector(':scope > [data-artwork-fee-cart-notice]');
        if (existing) {
          if (existing.textContent !== CONFIG.cartNotice) {
            existing.textContent = CONFIG.cartNotice;
          }
          return;
        }

        var notice = document.createElement('div');
        notice.className = 'artwork-fee-cart-notice';
        notice.setAttribute('data-artwork-fee-cart-notice', 'true');
        notice.textContent = CONFIG.cartNotice;
        parent.insertBefore(notice, subtotalRow);
      });
    });
  }

  function applyPresentation(state) {
    if (!state) return;

    lastPresentationState = state;
    hideFeeRows(state.cart);
    removeStaleHiddenMarkers();
    updateCartNotice(Boolean(state.feeRequired));
  }

  function schedulePresentation() {
    if (presentationTimer) window.clearTimeout(presentationTimer);
    presentationTimer = window.setTimeout(function () {
      presentationTimer = null;
      if (lastPresentationState) applyPresentation(lastPresentationState);
    }, 80);
  }

  function installPresentationObserver() {
    if (!window.MutationObserver || !document.documentElement || domObserver) return;

    domObserver = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (mutation) {
        return mutation.type === 'childList' && (mutation.addedNodes.length || mutation.removedNodes.length);
      });
      if (relevant) schedulePresentation();
    });

    domObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function emitCartEvents(cart, reason) {
    var detail = {
      cart: cart,
      source: SOURCE,
      reason: reason || 'sync'
    };

    ['cart:updated', 'cart:refresh', 'cart:update'].forEach(function (eventName) {
      try {
        document.dispatchEvent(
          new CustomEvent(eventName, {
            detail: detail,
            bubbles: true
          })
        );
      } catch (e) {
        // No-op.
      }
    });

    try {
      document.dispatchEvent(
        new CustomEvent('custom-artwork-fee:updated', {
          detail: detail,
          bubbles: true
        })
      );
    } catch (e) {
      // No-op.
    }
  }

  async function runSync(reason) {
    var cart = await getCart();
    var analysis = await analyzeCart(cart);
    var qty = analysis.customHatQuantity;
    var shouldHaveFee = qty >= CONFIG.minFeeQuantity && qty <= CONFIG.maxFeeQuantity;

    log('Sync:', {
      reason: reason,
      customHatQuantity: qty,
      shouldHaveFee: shouldHaveFee,
      feeLines: analysis.feeLines.length
    });

    var enforcement = await enforceFeeState(shouldHaveFee, analysis.feeLines);

    if (enforcement.changed) {
      await wait(CONFIG.cartSettleMs);
      cart = await getCart();
      analysis = await analyzeCart(cart);
      qty = analysis.customHatQuantity;
      shouldHaveFee = qty >= CONFIG.minFeeQuantity && qty <= CONFIG.maxFeeQuantity;
    }

    var state = {
      cart: cart,
      customHatQuantity: qty,
      feeRequired: shouldHaveFee,
      changed: enforcement.changed,
      feeAdded: enforcement.feeAdded
    };

    applyPresentation(state);

    if (enforcement.feeAdded) {
      openModal();
    }

    if (enforcement.changed) {
      emitCartEvents(cart, reason);
      // Theme events often re-render the cart after the event. Reapply our
      // visual presentation after that render without making another API call.
      window.setTimeout(schedulePresentation, 180);
      window.setTimeout(schedulePresentation, 450);
    }

    return state;
  }

  function syncArtworkFee(reason) {
    lastReason = reason || lastReason || 'manual';

    if (syncPromise) {
      syncQueued = true;
      return syncPromise;
    }

    syncPromise = runSync(lastReason)
      .catch(function (err) {
        errorLog('Synchronization failed.', err);
        return null;
      })
      .finally(function () {
        syncPromise = null;

        if (syncQueued) {
          syncQueued = false;
          scheduleSync('queued', 120);
        }
      });

    return syncPromise;
  }

  function scheduleSync(reason, delay) {
    lastReason = reason || lastReason || 'scheduled';

    if (syncTimer) {
      window.clearTimeout(syncTimer);
    }

    syncTimer = window.setTimeout(function () {
      syncTimer = null;
      syncArtworkFee(lastReason);
    }, typeof delay === 'number' ? delay : CONFIG.debounceMs);
  }

  function patchFetch() {
    if (!window.fetch || window.fetch.__artworkFeePatched) return;

    function patchedFetch() {
      var args = Array.prototype.slice.call(arguments);
      var watch = isCartMutationUrl(args[0]);

      return nativeFetch.apply(null, args).then(function (response) {
        if (watch && response && response.ok) {
          scheduleSync('fetch-cart-mutation', CONFIG.cartSettleMs);
        }
        return response;
      });
    }

    patchedFetch.__artworkFeePatched = true;
    patchedFetch.__artworkFeeOriginal = nativeFetch;
    window.fetch = patchedFetch;
  }

  function patchXHR() {
    if (!window.XMLHttpRequest || window.XMLHttpRequest.prototype.__artworkFeePatched) return;

    var proto = window.XMLHttpRequest.prototype;
    var nativeOpen = proto.open;
    var nativeSend = proto.send;

    proto.open = function () {
      var args = Array.prototype.slice.call(arguments);
      this.__artworkFeeWatch = isCartMutationUrl(args[1]);
      return nativeOpen.apply(this, args);
    };

    proto.send = function () {
      if (this.__artworkFeeWatch) {
        this.addEventListener(
          'load',
          function () {
            if (this.status >= 200 && this.status < 400) {
              scheduleSync('xhr-cart-mutation', CONFIG.cartSettleMs);
            }
          },
          { once: true }
        );
      }

      return nativeSend.apply(this, arguments);
    };

    proto.__artworkFeePatched = true;
  }

  function installThemeEventListeners() {
    var eventNames = [
      'cart:updated',
      'cart:change',
      'cart:refresh',
      'cart:update',
      'cart:build',
      'cart:open',
      'cart-drawer:open',
      'cart-drawer:opened',
      'product:added',
      'ajaxProduct:added',
      'added_to_cart'
    ];

    eventNames.forEach(function (eventName) {
      document.addEventListener(eventName, function (event) {
        if (event && event.detail && event.detail.source === SOURCE) return;
        scheduleSync('theme-event:' + eventName, CONFIG.debounceMs);
      });
    });
  }

  function installDomFallbacks() {
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.matches) return;

      if (
        target.matches(
          'input[name="updates[]"], input[name^="updates["], input[name="quantity"], .cart__quantity input, [data-cart-quantity], [data-quantity-input]'
        )
      ) {
        scheduleSync('cart-quantity-change', 700);
      }
    });

    document.addEventListener('click', function (event) {
      var target =
        event.target && event.target.closest
          ? event.target.closest(
              '[data-quantity-plus], [data-quantity-minus], [data-cart-remove], [data-remove-item], .cart-remove, .cart__remove, a[href*="/cart/change"], button[name="minus"], button[name="plus"]'
            )
          : null;

      if (target) {
        scheduleSync('cart-control-click', 700);
      }
    });

    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.getAttribute) return;

      var action = form.getAttribute('action') || '';
      if (/\/cart\/(add|change|update)/i.test(action) || /\/cart$/i.test(action)) {
        scheduleSync('cart-form-submit', 700);
      }
    });

    document.addEventListener('shopify:section:load', function () {
      scheduleSync('shopify-section-load', 350);
    });
  }

  window.CustomArtworkFee = Object.freeze({
    sync: function () {
      return syncArtworkFee('manual-api');
    },
    scheduleSync: scheduleSync,
    clearTagCache: function () {
      tagCache.clear();
    },
    showModal: openModal,
    closeModal: closeModal,
    config: CONFIG
  });

  patchFetch();
  patchXHR();
  installThemeEventListeners();
  installDomFallbacks();
  installModalKeyboardHandler();
  installPresentationObserver();
  ensureModalStyles();

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        ensureModal();
        scheduleSync('initial-dom-ready', 100);
      },
      { once: true }
    );
  } else {
    ensureModal();
    scheduleSync('initial-ready', 100);
  }
})();
